/**
 * Multi-format in-app book reader for the admin/handler proofreading workflow.
 * Renders PDF, DOCX, and EPUB inline with page/chapter tracking so the proofreader
 * can capture errors with current location metadata without leaving the page.
 *
 * Props:
 *   - bookId: the book id (used to construct the secure download URL)
 *   - mediaType: 'pdf' | 'docx' | 'epub' (auto-detected if not provided)
 *   - onPositionChange({ page, chapter, totalPages }): fired whenever the reader
 *       moves to a new page/chapter so the parent can auto-populate fault fields
 *   - onTextSelected(text): fired when the user selects text in the reader
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ReactReader } from 'react-reader';
import mammoth from 'mammoth';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Loader2, AlertTriangle, FileWarning } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// pdf.js worker — served from local public assets so no network hop / CDN issues
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const detectFormat = (url) => {
  if (!url) return null;
  const lower = url.split('?')[0].toLowerCase();
  if (lower.endsWith('.pdf')) return 'pdf';
  if (lower.endsWith('.docx')) return 'docx';
  if (lower.endsWith('.epub')) return 'epub';
  return null;
};

export const BookReader = ({ bookFileUrl, mediaType, onPositionChange, onTextSelected, bookmarkKey }) => {
  // Auto-detect format from URL if not explicitly passed
  const format = mediaType || detectFormat(bookFileUrl);

  // Selection handler shared across all readers
  const handleSelection = useCallback(() => {
    const sel = window.getSelection?.()?.toString().trim();
    if (sel && onTextSelected) onTextSelected(sel);
  }, [onTextSelected]);

  if (!bookFileUrl) {
    return (
      <div className="flex items-center justify-center h-96 border rounded bg-gray-50 text-gray-500">
        <AlertTriangle className="h-5 w-5 mr-2" />
        Geen lêer beskikbaar nie.
      </div>
    );
  }

  if (!format) {
    return (
      <div className="flex flex-col items-center justify-center h-96 border rounded bg-amber-50 text-amber-800 p-6">
        <FileWarning className="h-10 w-10 mb-2" />
        <p className="font-semibold">Onbekende lêer formaat</p>
        <p className="text-sm text-center mt-1">Slegs PDF, DOCX en EPUB kan in die blaaier vertoon word. Laai die lêer hieronder af om dit eksternal te lees.</p>
        <a href={bookFileUrl} target="_blank" rel="noreferrer"
           className="mt-3 inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
          Laai lêer af
        </a>
      </div>
    );
  }

  if (format === 'pdf') return <PdfReader url={bookFileUrl} onPositionChange={onPositionChange} onSelect={handleSelection} bookmarkKey={bookmarkKey} />;
  if (format === 'docx') return <DocxReader url={bookFileUrl} onSelect={handleSelection} />;
  if (format === 'epub') return <EpubReader url={bookFileUrl} onPositionChange={onPositionChange} onSelect={handleSelection} bookmarkKey={bookmarkKey} />;
  return null;
};

// ---------- PDF ----------
const PdfReader = ({ url, onPositionChange, onSelect, bookmarkKey }) => {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reset state whenever the URL changes — otherwise <Page> keeps asking the
  // previous (destroyed) pdf.js worker for pages and we get
  // "Cannot read properties of null (reading 'sendWithPromise')".
  useEffect(() => {
    setNumPages(null);
    setPage(1);
    setLoading(true);
    setError(null);
  }, [url]);

  // Restore saved reading page once the document reports its length.
  useEffect(() => {
    if (numPages && bookmarkKey) {
      try {
        const saved = parseInt(localStorage.getItem(`bookmark-pdf-${bookmarkKey}`), 10);
        if (saved && saved >= 1 && saved <= numPages) setPage(saved);
      } catch (e) { /* ignore */ }
    }
  }, [numPages, bookmarkKey]);

  useEffect(() => {
    if (numPages && onPositionChange) {
      onPositionChange({ page: String(page), chapter: '', totalPages: numPages });
    }
    if (numPages && bookmarkKey) {
      try { localStorage.setItem(`bookmark-pdf-${bookmarkKey}`, String(page)); } catch (e) { /* ignore */ }
    }
  }, [page, numPages, onPositionChange, bookmarkKey]);

  const goPrev = () => setPage(p => Math.max(1, p - 1));
  const goNext = () => setPage(p => Math.min(numPages || 1, p + 1));

  // Use httpHeaders={} so withCredentials does not interfere with Cloudinary CDN
  const docOpts = useMemo(() => ({ httpHeaders: {} }), []);

  return (
    <div className="border rounded bg-white" data-testid="pdf-reader">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
        <Button size="sm" variant="outline" onClick={goPrev} disabled={page <= 1} data-testid="pdf-prev-btn">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-sm font-medium">
          Bladsy {page} / {numPages || '?'}
        </div>
        <Button size="sm" variant="outline" onClick={goNext} disabled={page >= (numPages || 1)} data-testid="pdf-next-btn">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="overflow-auto" style={{ height: '70vh' }} onMouseUp={onSelect}>
        {error ? (
          <div className="p-8 text-red-700 bg-red-50">PDF kon nie laai nie: {error}</div>
        ) : (
          // `key={url}` forces a full remount of the Document tree when the URL
          // changes so the old pdf.js WorkerTransport is properly torn down
          // before the new one starts loading — prevents stale-getPage races.
          <Document
            key={url}
            file={url}
            options={docOpts}
            onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoading(false); }}
            onLoadError={(e) => { setError(e.message); setLoading(false); }}
            loading={<div className="p-8 flex items-center text-gray-500"><Loader2 className="h-5 w-5 mr-2 animate-spin" /> PDF laai...</div>}
          >
            {/* Only render a Page once the document has finished loading and
                announces numPages. Without this guard, react-pdf tries to
                resolve <Page pageNumber={1}/> against a not-yet-ready worker
                and throws sendWithPromise on null. */}
            {numPages && !loading && (
              <Page pageNumber={page} width={700} renderTextLayer renderAnnotationLayer={false} />
            )}
          </Document>
        )}
        {loading && !error && <div className="p-8 text-gray-500">Laai PDF...</div>}
      </div>
    </div>
  );
};

// ---------- DOCX ----------
const DocxReader = ({ url, onSelect }) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then(buf => mammoth.convertToHtml({ arrayBuffer: buf }))
      .then(({ value }) => { if (!cancelled) { setHtml(value); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div className="border rounded bg-white" data-testid="docx-reader">
      <div className="px-3 py-2 bg-gray-50 border-b text-sm font-medium">
        DOCX dokument (rol af om te lees)
      </div>
      <div className="overflow-auto p-6 prose max-w-none" style={{ height: '70vh' }} onMouseUp={onSelect}>
        {loading && <div className="text-gray-500 flex items-center"><Loader2 className="h-5 w-5 mr-2 animate-spin" /> DOCX laai...</div>}
        {error && <div className="text-red-700 bg-red-50 p-3 rounded">DOCX kon nie laai nie: {error}</div>}
        {!loading && !error && (
          // Writer-supplied DOCX content is converted to HTML by mammoth, then
          // sanitised here to neutralise any embedded <script>/onerror payloads
          // before rendering inside the proofreader view.
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }) }} />
        )}
      </div>
    </div>
  );
};

// ---------- EPUB ----------
const EpubReader = ({ url, onPositionChange, onSelect, bookmarkKey }) => {
  const [location, setLocation] = useState(() => {
    if (bookmarkKey) {
      try { return localStorage.getItem(`bookmark-epub-${bookmarkKey}`) || null; } catch (e) { return null; }
    }
    return null;
  });
  const [chapterLabel, setChapterLabel] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [renditionReady, setRenditionReady] = useState(false);

  useEffect(() => {
    if (onPositionChange) {
      onPositionChange({ page: '', chapter: chapterLabel, totalPages: null });
    }
  }, [chapterLabel, onPositionChange]);

  // react-reader has no built-in error/timeout signal — it just spins forever if
  // the EPUB URL fails (401, CORS, network, corrupt file). Time out after 20s
  // and show a clear error so the user knows something is wrong.
  useEffect(() => {
    setLoadError(null);
    setRenditionReady(false);
    const timer = setTimeout(() => {
      if (!renditionReady) {
        setLoadError('Die EPUB lêer kon nie laai nie. Dit kan beteken die lêer is beskadig, ontbreek, of die bediener kon nie die lêer aflewer nie.');
      }
    }, 20000);
    return () => clearTimeout(timer);
  }, [url, renditionReady]);

  if (loadError) {
    return (
      <div className="border rounded bg-red-50 p-6" data-testid="epub-reader-error">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-red-800">Kon nie EPUB laai nie</p>
            <p className="text-sm text-red-700 mt-1">{loadError}</p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setLoadError(null); setRenditionReady(false); }} data-testid="epub-retry-btn">
                Probeer weer
              </Button>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Laai lêer af
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded bg-white" data-testid="epub-reader">
      <div className="px-3 py-2 bg-gray-50 border-b text-sm font-medium">
        EPUB — {chapterLabel || 'Begin lees'}
      </div>
      <div style={{ height: '70vh', position: 'relative' }} onMouseUp={onSelect}>
        <ReactReader
          url={url}
          location={location}
          locationChanged={(loc) => {
            setLocation(loc);
            if (bookmarkKey && loc) {
              try { localStorage.setItem(`bookmark-epub-${bookmarkKey}`, loc); } catch (e) { /* ignore */ }
            }
          }}
          tocChanged={() => { /* full TOC available if we ever need it */ }}
          getRendition={(rendition) => {
            setRenditionReady(true);
            rendition.on('relocated', (loc) => {
              try {
                const navItem = rendition.book.navigation.get(loc.start.href);
                if (navItem?.label) setChapterLabel(navItem.label.trim());
              } catch (_) { /* ignore */ }
            });
          }}
          epubInitOptions={{ openAs: 'epub' }}
        />
      </div>
    </div>
  );
};

export default BookReader;
